import { Alert, App as AntApp, Button, Grid, Space, Table, Tag, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { TeacherDrawer } from "../components/TeacherDrawer.tsx";
import { LinkUserModal } from "../components/LinkUserModal.tsx";
import { AccountCell, ProfileActions, StatusTag } from "../components/ProfileActions.tsx";
import { ProfileListToolbar, type ActiveFilter } from "../components/ProfileListToolbar.tsx";

type TeacherDetails = components["schemas"]["TeacherDetails"];

export function TeachersPage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const screens = Grid.useBreakpoint();
  const canManage = hasPermission("people.manage");
  const canLink = canManage && hasPermission("identity.manage");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ActiveFilter>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [editing, setEditing] = useState<TeacherDetails | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [linking, setLinking] = useState<TeacherDetails | null>(null);

  const query = useQuery({
    queryKey: ["teachers", { search, filter, page, pageSize }],
    queryFn: async () => {
      const { data, error } = await api.GET("/teachers", {
        params: {
          query: { ...(search ? { Search: search } : {}), ...(filter === "all" ? {} : { IsActive: filter === "active" }), Page: page, PageSize: pageSize },
        },
      });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    placeholderData: keepPreviousData,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["teachers"] });
  const showError = (err: unknown) => void message.error((err instanceof ApiError ? err.messages(t("app.error")) : [t("app.error")]).join(" "));

  const toggle = useMutation({
    mutationFn: async (teacher: TeacherDetails) => {
      const path = teacher.isActive ? "/teachers/{id}/deactivate" : "/teachers/{id}/reactivate";
      const result = await api.POST(path, { params: { path: { id: teacher.id } } });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async (saved) => {
      await refresh();
      void message.success(t(saved.isActive ? "teachers.reactivated" : "teachers.deactivated", { name: saved.fullName }));
    },
    onError: showError,
  });

  const unlink = useMutation({
    mutationFn: async (teacher: TeacherDetails) => {
      const result = await api.POST("/teachers/{id}/unlink-user", { params: { path: { id: teacher.id } } });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async (saved) => {
      await refresh();
      void message.success(t("person.unlinked", { name: saved.fullName }));
    },
    onError: showError,
  });

  const linkUser = async (userId: string) => {
    if (!linking) return;
    const result = await api.POST("/teachers/{id}/link-user", { params: { path: { id: linking.id } }, body: { userId } });
    if (!result.data) throw new ApiError(result.response.status, result.error);
    await refresh();
    void message.success(t("person.linked", { name: result.data.fullName }));
    setLinking(null);
  };

  const columns = [
    {
      title: t("person.columns.name"),
      key: "name",
      render: (_: unknown, teacher: TeacherDetails) => <Typography.Text strong>{teacher.fullName}</Typography.Text>,
    },
    {
      title: t("teachers.subjects"),
      key: "subjects",
      render: (_: unknown, teacher: TeacherDetails) =>
        teacher.subjects.length === 0 ? (
          <Typography.Text type="secondary">{t("teachers.noSubjects")}</Typography.Text>
        ) : (
          <Space size={[4, 4]} wrap>
            {teacher.subjects.map((s) => (
              <Tag key={s.id} color="blue" style={{ marginInlineEnd: 0 }}>
                {s.name}
              </Tag>
            ))}
          </Space>
        ),
    },
    { title: t("person.columns.phone"), dataIndex: "phone", key: "phone", responsive: ["md" as const], width: 150 },
    { title: t("person.columns.status"), key: "status", width: 110, render: (_: unknown, teacher: TeacherDetails) => <StatusTag active={teacher.isActive} /> },
    { title: t("person.columns.account"), key: "account", responsive: ["lg" as const], render: (_: unknown, teacher: TeacherDetails) => <AccountCell user={teacher.user} /> },
    ...(canManage
      ? [
          {
            title: t("person.columns.actions"),
            key: "actions",
            width: screens.lg ? 360 : 120,
            render: (_: unknown, teacher: TeacherDetails) => (
              <ProfileActions
                row={teacher}
                deactivateConfirm={t("teachers.deactivateConfirm", { name: teacher.fullName })}
                reactivateConfirm={t("teachers.reactivateConfirm", { name: teacher.fullName })}
                canLink={canLink}
                busy={toggle.isPending && toggle.variables?.id === teacher.id}
                onEdit={() => {
                  setEditing(teacher);
                  setDrawerOpen(true);
                }}
                onToggle={() => toggle.mutate(teacher)}
                onLink={() => setLinking(teacher)}
                onUnlink={() => unlink.mutate(teacher)}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <ProfileListToolbar
        title={t("teachers.title")}
        searchPlaceholder={t("teachers.search")}
        filter={filter}
        onFilter={(value) => {
          setFilter(value);
          setPage(1);
        }}
        onSearch={(value) => {
          setSearch(value);
          setPage(1);
        }}
        extra={
          canManage && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                setDrawerOpen(true);
              }}
            >
              {t("teachers.create")}
            </Button>
          )
        }
      />
      {query.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>} />}
      <Table<TeacherDetails>
        rowKey="id"
        size={screens.md ? "middle" : "small"}
        loading={query.isPending}
        columns={columns}
        dataSource={query.data?.items ?? []}
        locale={{ emptyText: t("teachers.empty") }}
        scroll={{ x: 560 }}
        pagination={{
          current: page,
          pageSize,
          total: Number(query.data?.totalCount ?? 0),
          showSizeChanger: true,
          showTotal: (total) => t("person.total", { count: total }),
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize);
          },
        }}
      />
      <TeacherDrawer
        open={drawerOpen}
        teacher={editing}
        onClose={() => setDrawerOpen(false)}
        onSaved={(saved, created) => {
          setDrawerOpen(false);
          void message.success(t(created ? "teachers.created" : "teachers.saved", { name: saved.fullName }));
        }}
      />
      <LinkUserModal profileName={linking?.fullName ?? null} onClose={() => setLinking(null)} submit={linkUser} />
    </Space>
  );
}
